import React, { useState, useEffect, Suspense, useContext } from "react";

import { t } from "i18next";
import { Trans } from "react-i18next";
import { Link, Outlet, useNavigate } from "react-router-dom";

import Navbar from "../components/Navbars/Navbar";
import SideMenu from "../components/SideMenus/SideMenu";
// LABSTORE_ACCESS -- isLabstoreAuthorized
import { ROLES } from "../utils/constants";
import { isAuthorized } from "../utils/RolesHelpers";
import Footer from "../components/Footers/Footer";
import SimpleNotification from "../components/Notifications/SimpleNotification";
import { addDateToDate, longDate } from "../utils/DateHelper";
import LegalSettingsAPI from "../API/legalSettings.api";
import { setItem } from "../utils/StorageHelpers";
import LoadingOverlay from "../components/Loaders/LoadingOverlay";
import { GLOBAL_ROUTES } from "../utils/RoutesConstant";
import { UserContext } from "../lib/MainContext";

const Admin = () => {
    const { user: currentUser, setUser } = useContext(UserContext);
    const [isLegalConfirmationLoading, setIsLegalConfirmationLoading] =
        useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (currentUser && isAccessRestricted())
            navigate(GLOBAL_ROUTES.LicenseExpired);
    }, [currentUser]);

    const getExtendedTimeEnd = () => {
        const endOfLicense = currentUser?.organization?.endOfLicense;
        const extendedDeadline = addDateToDate(new Date(endOfLicense), 30);

        return { date: longDate(extendedDeadline) };
    };

    const isAccessRestricted = () => {
        const userOrganization = currentUser?.organization;
        return (
            !userOrganization?.isLicenseValid &&
            !userOrganization?.isLicenseInExtendedPeriod &&
            !currentUser.role === ROLES.superAdmin
        );
    };

    const isExtendedPeriodValid = () => {
        const userOrganization = currentUser?.organization;
        return (
            !userOrganization?.isLicenseValid &&
            userOrganization?.isLicenseInExtendedPeriod
        );
    };

    const isLegalSettingsAccepted = () => {
        if (
            !isAuthorized(
                [ROLES.support, ROLES.partnerAdmin],
                [currentUser.role]
            )
        )
            return true;

        return currentUser?.isLegalSettingsAccepted;
    };

    const confirmLegalSettings = async () => {
        setIsLegalConfirmationLoading(true);
        const result = await LegalSettingsAPI.confirm();

        if (result?.modified) {
            setItem("user", {
                ...currentUser,
                isLegalSettingsAccepted: true,
            });
            setUser({ ...currentUser, isLegalSettingsAccepted: true });
        }

        setIsLegalConfirmationLoading(false);
    };

    const renderLegalSettingsButton = () => {
        const buttonContent = isLegalConfirmationLoading ? (
            <>
                <span className="animate-spin mr-3 inline-flex">
                    <i className="fas fa-sync"></i>
                </span>
                <Trans i18nKey="requesting">Requesting...</Trans>
            </>
        ) : (
            t("Accept")
        );

        return (
            <button
                onClick={confirmLegalSettings}
                className={`secondary-green-btn ${
                    isLegalConfirmationLoading ? "disabled" : ""
                }`}
                disabled={isLegalConfirmationLoading}
            >
                {buttonContent}
            </button>
        );
    };

    const getLegalSettingsMessage = () => {
        const expiryDate = longDate(
            currentUser?.legalSettings?.confirmationExpiryDate || Date.now()
        );
        const termsAndConditions =
            getLegalSettingsLinkFor("termsAndConditions");

        const endUserAgreement = getLegalSettingsLinkFor("endUserAgreement");

        const html = t("legalSettingsNotification", {
            expiryDate,
        })
            .replace("{{termsandConditions}}", termsAndConditions)
            .replace("{{endUserAgreement}}", endUserAgreement);

        return (
            <p
                dangerouslySetInnerHTML={{
                    __html: ` <i class="fas fa-exclamation-triangle"></i> ${html}`,
                }}
            />
        );
    };

    const getLegalSettingsLinkFor = (key = "termsAndConditions") => {
        const links = currentUser?.legalSettings?.links;

        return `<a
                class="underline text-blue-600"
                href=${links?.[key] || "#"}
                target="_blank"
                rel="noreferrer"
            >
                ${t(key)}
            </a>`;
    };

    const getExtendedMessage = () => {
        if (
            isAuthorized(
                [ROLES.support, ROLES.partnerAdmin],
                [currentUser.role]
            )
        ) {
            return (
                <Link
                    className="underline text-blue-600"
                    to={GLOBAL_ROUTES.LicenseExpired}
                >
                    {t("renewNow")}
                </Link>
            );
        }

        return t("contactAdmin");
    };

    if (!currentUser) return <></>;
    return (
        <>
            <Navbar />
            <SideMenu />

            <div className="license-notification sticky top-0 z-98 pt-16 w-full">
                {isExtendedPeriodValid() && (
                    <SimpleNotification
                        message={
                            <>
                                <Trans i18nKey="licenseExpiredNotification">
                                    License expired must renew before
                                    {getExtendedTimeEnd()}
                                </Trans>
                                {getExtendedMessage()}
                            </>
                        }
                        className="main-content banner"
                    />
                )}
                {!isLegalSettingsAccepted() && (
                    <SimpleNotification
                        component={
                            <div className="text-xs w-full flex justify-between gap-1">
                                {getLegalSettingsMessage()}
                                <div className="flex items-start">
                                    {renderLegalSettingsButton()}
                                </div>
                            </div>
                        }
                        className="main-content banner"
                        marginBottom=""
                    />
                )}
            </div>
            {/* Main Content */}
            <main className="main-content expired-license" id="main-content">
                <Suspense
                    fallback={
                        <LoadingOverlay active>
                            <div className="suspense-fallback" />
                        </LoadingOverlay>
                    }
                >
                    <Outlet />
                </Suspense>
            </main>
            <Footer />
        </>
    );
};

export default Admin;
